import React, { useEffect, useMemo, useRef } from 'react';
import { StepProps } from '../MultiStepModal';
import { useAuth } from '../../../contexts/AuthContext';
import { jobsApi } from '../../../utils/api';
import SignaturePad from '../../../pages/engineer/SignaturePad';

export default function CertificateTemplateFillGroupStep({ data, onDataChange }: StepProps) {
  const { user } = useAuth();
  const lastJobLookupQueryRef = useRef<string>('');
  const groupName = String((data as any)?.activeGroupName || '').trim();
  const template = (data as any)?.selectedTemplate;
  const filledDataRaw = (data as any)?.filledData;
  const templateFields: any[] = useMemo(() => {
    return Array.isArray(template?.fields) ? template.fields : [];
  }, [template]);

  const filledData: Record<string, any> = useMemo(() => {
    return filledDataRaw && typeof filledDataRaw === 'object' ? filledDataRaw : {};
  }, [filledDataRaw]);

  const fields = useMemo(() => {
    const g = groupName || 'Ungrouped';
    return templateFields
      .filter((f: any) => {
        const gn = String(f?.groupName || '').trim() || 'Ungrouped';
        return gn === g;
      })
      .slice()
      .sort((a: any, b: any) => Number(a?.orderIndex ?? 0) - Number(b?.orderIndex ?? 0));
  }, [templateFields, groupName]);

  const autofillCtx = useMemo(() => {
    const customerName = String((data as any)?.customerName || '').trim();
    const customerEmail = String((data as any)?.customerEmail || '').trim();
    const customerPhone = String((data as any)?.customerPhone || '').trim();
    const customerAddress = String((data as any)?.customerAddress || '').trim();
    const customerPostcode = String((data as any)?.postcode || '').trim();

    const siteName = String((data as any)?.siteName || '').trim();
    const siteAddress = String((data as any)?.siteAddress || '').trim();
    const sitePostcode = String((data as any)?.sitePostcode || (data as any)?.sitePostcode || '').trim();
    const siteType = String((data as any)?.siteType || '').trim();

    const engineerName = String((user as any)?.name || '').trim();
    const engineerEmail = String((user as any)?.email || '').trim();
    const engineerPhone = String((user as any)?.phone || '').trim();
    const engineerGasSafeNumber = String((user as any)?.gasSafeNumber || '').trim();

    return {
      customerName,
      customerEmail,
      customerPhone,
      customerAddress,
      customerPostcode,
      siteName,
      siteAddress,
      sitePostcode,
      siteType,
      engineerName,
      engineerEmail,
      engineerPhone,
      engineerGasSafeNumber,
    };
  }, [data, user]);

  useEffect(() => {
    const normalize = (v: any) =>
      String(v || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_');

    const resolve = (field: any): any => {
      const semantic = normalize(field?.semanticTag);
      const pdf = normalize(field?.pdfFieldName);
      const label = normalize(field?.label || field?.displayLabel);
      const hay = `${semantic} ${pdf} ${label}`;

      if (/(customer|client)_(name|full_name)/.test(hay)) return autofillCtx.customerName;
      if (/(customer|client)_email/.test(hay)) return autofillCtx.customerEmail;
      if (/(customer|client)_(phone|tel|telephone|mobile)/.test(hay)) return autofillCtx.customerPhone;
      if (/(customer|client)_(address|addr)/.test(hay)) return autofillCtx.customerAddress;
      if (/(customer|client)_(postcode|post_code|zip)/.test(hay)) return autofillCtx.customerPostcode;

      if (/(site|property)_(name|title)/.test(hay)) return autofillCtx.siteName;
      if (/(site|property)_(address|addr)/.test(hay)) return autofillCtx.siteAddress || autofillCtx.customerAddress;
      if (/(site|property)_(postcode|post_code|zip)/.test(hay)) return autofillCtx.sitePostcode || autofillCtx.customerPostcode;
      if (/(site|property)_(type|category)/.test(hay)) return autofillCtx.siteType;

      if (/(engineer|inspector|technician)_(name|full_name)/.test(hay)) return autofillCtx.engineerName;
      if (/(engineer|inspector|technician)_email/.test(hay)) return autofillCtx.engineerEmail;
      if (/(engineer|inspector|technician)_(phone|tel|telephone|mobile)/.test(hay)) return autofillCtx.engineerPhone;
      if (/(gassafe|gas_safe|gas)_(number|id|registration)/.test(hay)) return autofillCtx.engineerGasSafeNumber;

      return undefined;
    };

    const current = filledDataRaw && typeof filledDataRaw === 'object' ? (filledDataRaw as Record<string, any>) : {};
    const next: Record<string, any> = { ...current };
    let changed = false;

    for (const f of fields) {
      const key = String(f?.pdfFieldName || '').trim();
      if (!key) continue;
      const existing = next[key];
      const hasExisting = existing !== undefined && existing !== null && String(existing).trim() !== '';
      if (hasExisting) continue;

      const v = resolve(f);
      if (v === undefined || v === null || String(v).trim() === '') continue;

      next[key] = v;
      changed = true;
    }

    if (changed) {
      onDataChange({ filledData: next });
    }
  }, [autofillCtx, fields, filledDataRaw, onDataChange]);

  useEffect(() => {
    const normalize = (v: any) =>
      String(v || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_');

    const looksLikeJobNumberField = (field: any) => {
      const semantic = normalize(field?.semanticTag);
      const pdf = normalize(field?.pdfFieldName);
      const label = normalize(field?.label || field?.displayLabel);
      const hay = `${semantic} ${pdf} ${label}`;
      return /(job|work_order|workorder|wo)_(number|no|id|ref|reference)/.test(hay) || /(job_number|job_no|work_order)/.test(hay);
    };

    const current = filledDataRaw && typeof filledDataRaw === 'object' ? (filledDataRaw as Record<string, any>) : {};
    const jobField = fields.find((f) => looksLikeJobNumberField(f));
    if (!jobField) return;

    const jobKey = String(jobField?.pdfFieldName || '').trim();
    if (!jobKey) return;

    const q = String(current[jobKey] || '').trim();
    if (!q) return;
    if (q === lastJobLookupQueryRef.current) return;

    const handle = setTimeout(async () => {
      try {
        const res = await jobsApi.lookup(q);
        const payload = res.data;
        const dataPayload = payload?.data || payload;
        const job = dataPayload?.job || null;
        const matches = Array.isArray(dataPayload?.matches) ? dataPayload.matches : [];

        if (!job && matches.length !== 1) return;
        const resolved = job || matches[0];
        if (!resolved?.id) return;

        lastJobLookupQueryRef.current = q;

        const patch: any = {
          jobId: String(resolved.id),
        };

        if (resolved.site?.id) {
          patch.siteId = String(resolved.site.id);
          patch.siteName = String(resolved.site.name || '').trim();
          patch.siteAddress = String(resolved.site.address || '').trim();
          patch.sitePostcode = String(resolved.site.postcode || '').trim();
        }

        if (!data.customerId && resolved.client?.id) {
          patch.customerId = String(resolved.client.id);
          patch.isNewCustomer = false;
        }

        if (!String((data as any)?.customerName || '').trim()) patch.customerName = String(resolved.client?.name || '').trim();
        if (!String((data as any)?.customerEmail || '').trim()) patch.customerEmail = String(resolved.client?.email || '').trim();
        if (!String((data as any)?.customerPhone || '').trim()) patch.customerPhone = String(resolved.client?.phone || '').trim();
        if (!String((data as any)?.customerAddress || '').trim()) patch.customerAddress = String(resolved.client?.address || '').trim();
        if (!String((data as any)?.postcode || '').trim()) patch.postcode = String(resolved.client?.postcode || '').trim();

        onDataChange(patch);
      } catch {
        // ignore
      } finally {
      }
    }, 450);

    return () => clearTimeout(handle);
  }, [data, fields, filledDataRaw, onDataChange]);

  const setValue = (pdfFieldName: string, value: any) => {
    onDataChange({
      filledData: {
        ...filledData,
        [pdfFieldName]: value,
      },
    });
  };

  if (!groupName) {
    return (
      <div className="step-form">
        <div className="step-header">
          <h3 className="step-form-title">Template fields</h3>
          <p className="step-form-subtitle">No group selected.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">{groupName}</h3>
        <p className="step-form-subtitle">Fill in this section of the certificate.</p>
      </div>

      <div className="step-form-content">
        {fields.length === 0 ? (
          <div className="text-sm text-[#686E6C]">No fields in this group.</div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {fields.map((f: any) => {
              const pdfFieldName = String(f?.pdfFieldName || '').trim();
              if (!pdfFieldName) return null;

              const label = String(f?.displayLabel || pdfFieldName);
              const fieldType = String(f?.fieldType || 'text');
              const required = Boolean(f?.required);

              const v = filledData[pdfFieldName];

              if (fieldType === 'checkbox') {
                return (
                  <label key={pdfFieldName} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(v)}
                      onChange={(e) => setValue(pdfFieldName, e.target.checked)}
                    />
                    <span className="text-[#181818]">
                      {label}
                      {required ? ' *' : ''}
                    </span>
                  </label>
                );
              }

              if (fieldType === 'signature') {
                return (
                  <div key={pdfFieldName} className="form-group">
                    <div className="form-label">
                      {label}
                      {required ? ' *' : ''}
                    </div>
                    <SignaturePad
                      height={160}
                      value={typeof v === 'string' ? v : null}
                      onChange={(dataUrl) => setValue(pdfFieldName, dataUrl)}
                    />
                  </div>
                );
              }

              return (
                <div key={pdfFieldName} className="form-group">
                  <label className="form-label">
                    {label}
                    {required ? ' *' : ''}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={typeof v === 'string' || typeof v === 'number' ? String(v) : ''}
                    onChange={(e) => setValue(pdfFieldName, e.target.value)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
